<template>
  <article
    class="docs-page"
    :class="{ 'docs-page--dark': $vuetify.theme.dark }"
    lang="en"
  >
    <a
      class="docs-skip"
      href="#docs-main"
    >Skip to documentation</a>
    <header class="docs-header">
      <router-link
        class="docs-brand"
        :to="docLink()"
      >
        Self Biography / Documentation
      </router-link>
      <label
        class="docs-search-label"
        for="docs-search"
      >What would you like to do?</label>
      <div class="docs-search">
        <v-icon aria-hidden="true">
          {{ searchIcon }}
        </v-icon>
        <input
          id="docs-search"
          v-model="search"
          type="search"
          placeholder="Search Self Biography Documentation…"
          autocomplete="off"
        />
        <button
          v-if="search"
          type="button"
          @click="search = ''"
        >
          Clear
        </button>
      </div>
      <div
        class="docs-devices"
        role="group"
        aria-label="Choose your device"
      >
        <span>Instructions for</span>
        <button
          v-for="item in devices"
          :key="item.id"
          type="button"
          :aria-pressed="device === item.id ? 'true' : 'false'"
          @click="chooseDevice(item.id)"
        >
          {{ item.label }}
        </button>
      </div>
    </header>
    <div class="docs-layout">
      <aside class="docs-sidebar">
        <details :open="$vuetify.breakpoint.mdAndUp">
          <summary>Browse documentation</summary>
          <nav aria-label="Documentation categories">
            <router-link
              class="docs-overview-link"
              :to="docLink()"
              :aria-current="!article && !missing ? 'page' : undefined"
            >
              Overview
            </router-link>
            <details
              v-for="category in content.categories"
              :key="category.id"
              :open="!!article && article.category === category.id"
            >
              <summary>{{ category.title }}</summary>
              <router-link
                v-for="item in categoryArticles(category.id)"
                :key="item.id"
                :to="docLink(item.id)"
                :aria-current="article && article.id === item.id ? 'page' : undefined"
              >
                {{ item.title }}
              </router-link>
            </details>
          </nav>
        </details>
        <router-link
          class="docs-sidebar-help"
          to="/pages/support"
        >
          Need a hand? Contact support →
        </router-link>
      </aside>
      <div
        id="docs-main"
        ref="main"
        class="docs-main"
        tabindex="-1"
      >
        <template v-if="search.trim()">
          <p class="docs-eyebrow">
            Search the manual
          </p>
          <h1>Search results</h1>
          <p
            role="status"
            aria-live="polite"
          >
            {{ results.length }} {{ results.length === 1 ? 'guide' : 'guides' }} found for “{{ search.trim() }}”.
          </p>
          <div class="docs-results">
            <router-link
              v-for="item in results"
              :key="item.id"
              :to="docLink(item.id)"
              @click.native="search = ''"
            >
              <span class="docs-eyebrow">{{ categoryTitle(item.category) }}</span>
              <h2>{{ item.title }}</h2>
              <p>{{ item.summary }}</p>
            </router-link>
          </div>
          <div
            v-if="!results.length"
            class="docs-note"
          >
            <h2>Try a different phrase</h2>
            <p>Use a short topic such as “private”, “photos”, “iPhone”, or “credits”. You can also browse the categories or ask Support for help.</p>
            <router-link to="/pages/support">
              Contact support →
            </router-link>
          </div>
        </template>
        <template v-else-if="article">
          <nav
            class="docs-breadcrumbs"
            aria-label="Breadcrumb"
          >
            <router-link :to="docLink()">
              Documentation
            </router-link><span aria-hidden="true">/</span><span>{{ categoryTitle(article.category) }}</span>
          </nav>
          <h1>{{ article.title }}</h1>
          <p class="docs-lead">
            {{ article.summary }}
          </p>
          <div
            v-if="deviceAdvice"
            class="docs-device-note"
          >
            <strong>{{ deviceLabel }}</strong>
            <p>{{ deviceAdvice }}</p>
          </div>
          <nav
            class="docs-toc"
            aria-label="On this guide"
          >
            <strong>On this guide</strong>
            <a
              v-for="section in article.sections"
              :key="section.id"
              :href="`#${section.id}`"
            >{{ section.title }}</a>
          </nav>
          <section
            v-for="section in article.sections"
            :key="section.id"
            :aria-labelledby="section.id"
            class="docs-section"
          >
            <h2 :id="section.id">
              {{ section.title }}
            </h2>
            <p
              v-for="(paragraph, index) in section.paragraphs"
              :key="index"
            >
              {{ paragraph }}
            </p>
            <ol v-if="section.steps.length">
              <li
                v-for="step in section.steps"
                :key="step"
              >
                {{ step }}
              </li>
            </ol>
          </section>
          <div
            v-if="article.links.length"
            class="docs-actions"
          >
            <router-link
              v-for="link in article.links"
              :key="link.to"
              :to="link.to"
            >
              {{ link.label }} →
            </router-link>
          </div>
          <aside
            v-if="article.related.length"
            class="docs-related"
            aria-label="Related guides"
          >
            <h2>Related guides</h2>
            <router-link
              v-for="id in article.related"
              :key="id"
              :to="docLink(id)"
            >
              {{ articleTitle(id) }} →
            </router-link>
          </aside>
          <div class="docs-feedback">
            <p>Still need help with this?</p><router-link to="/pages/support">
              Contact support →
            </router-link>
          </div>
        </template>
        <template v-else-if="missing">
          <h1>Guide not found</h1>
          <p>This documentation link may have changed. Search above or return to the overview.</p>
          <router-link :to="docLink()">
            Browse all documentation →
          </router-link>
        </template>
        <template v-else>
          <p class="docs-eyebrow">
            Your living manual
          </p>
          <h1>Everything you need to use Self Biography.</h1>
          <p class="docs-lead">
            Build, preserve, and share your life story—one memory at a time.
          </p>
          <p>Find step-by-step instructions, feature descriptions, device-specific help, and answers to common questions. Start anywhere. You don't have to build your life in order.</p>
          <div class="docs-start">
            <strong>New here? Start with one memory.</strong>
            <p>You don't need to organize your entire photo collection before you begin. Add a small piece, give it context, and keep going.</p>
            <router-link :to="docLink('first-memory')">
              Create your first Memory Bit →
            </router-link>
          </div>
          <h2 class="docs-browse-title">
            Browse by topic
          </h2>
          <div class="docs-categories">
            <section
              v-for="category in content.categories"
              :key="category.id"
              class="docs-category"
            >
              <router-link
                class="docs-category-title"
                :to="docLink(categoryArticles(category.id)[0].id)"
              >
                <h3>{{ category.title }} <span aria-hidden="true">↗</span></h3>
              </router-link>
              <p>{{ category.summary }}</p>
              <ul>
                <li
                  v-for="item in categoryArticles(category.id)"
                  :key="item.id"
                >
                  <router-link :to="docLink(item.id)">
                    {{ item.title }}
                  </router-link>
                </li>
              </ul>
            </section>
          </div>
          <div class="docs-feedback">
            <p>Looking for the bigger picture?</p>
            <router-link to="/pages/self-biography">
              About Self Biography →
            </router-link>
            <router-link to="/pages/faq">
              Frequently asked questions →
            </router-link>
          </div>
        </template>
      </div>
    </div>
  </article>
</template>
<script>
import { mdiMagnify } from '@mdi/js'
import content from '@/content/selfBiographyDocumentation.json'

function initialDevice() {
  if (/iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) return 'ios'
  if (/Android/.test(navigator.userAgent)) return 'android'

  return 'desktop'
}

export default {
  name: 'SelfBiographyDocumentation',
  data: () => ({
    content,
    search: '',
    searchIcon: mdiMagnify,
    previousTitle: '',
    defaultDevice: initialDevice(),
    devices: [{ id: 'desktop', label: 'Desktop / Mac & PC' }, { id: 'ios', label: 'iPhone & iPad' }, { id: 'android', label: 'Android' }, { id: 'tablet', label: 'Tablet' }],
  }),
  computed: {
    article() { return this.content.articles.find(item => item.id === this.$route.params.article) },
    missing() { return !!this.$route.params.article && !this.article },
    device() { return this.devices.some(item => item.id === this.$route.query.device) ? this.$route.query.device : this.defaultDevice },
    deviceLabel() { return this.devices.find(item => item.id === this.device).label },
    deviceAdvice() { return this.article && this.article.devices[this.device] },
    results() {
      const words = this.search.toLowerCase().trim().split(/\s+/)

      return this.content.articles.filter(item => {
        const text = [item.title, item.summary, this.categoryTitle(item.category), ...item.sections.map(section => [section.title, ...section.paragraphs, ...section.steps].join(' ')), ...Object.values(item.devices)].join(' ').toLowerCase()

        return words.every(word => text.includes(word))
      })
    },
  },
  watch: {
    '$route.params.article': function articleChanged() {
      this.search = ''
      this.updateTitle()
      this.$nextTick(() => {
        this.$refs.main.focus({ preventScroll: true })
        this.$refs.main.scrollIntoView({ block: 'start' })
      })
    },
  },
  mounted() {
    this.previousTitle = document.title
    this.updateTitle()
    this.$nextTick(() => {
      const id = this.$route.hash.slice(1)
      const target = this.article && this.article.sections.some(section => section.id === id) && document.getElementById(id)
      if (target) target.scrollIntoView()
      else window.scrollTo(0, 0)
    })
  },
  beforeDestroy() { document.title = this.previousTitle },
  methods: {
    docLink(id) { return { name: 'page-documentation', params: id ? { article: id } : {}, query: { device: this.device } } },
    categoryArticles(id) { return this.content.articles.filter(item => item.category === id) },
    categoryTitle(id) { return this.content.categories.find(item => item.id === id).title },
    articleTitle(id) { return this.content.articles.find(item => item.id === id).title },
    chooseDevice(device) {
      if (device !== this.device) this.$router.replace({ path: this.$route.path, query: { ...this.$route.query, device }, hash: this.$route.hash })
    },
    updateTitle() { document.title = `${this.article ? this.article.title : 'Documentation'} | Self Biography` },
  },
}
</script>
<style lang="scss" scoped>
.docs-page {
  --docs-text: #5e5669; --docs-heading: #393345; --docs-accent: #713ac7; --docs-surface: #fff; --docs-tint: #f3eefb; --docs-line: rgba(94,86,105,.16);
  max-width: 1220px; margin: 28px auto 48px; color: var(--docs-text); font-size: 16px; line-height: 1.75; text-align: left;
  &--dark { --docs-text: #d0cadf; --docs-heading: #eee9fa; --docs-accent: #c6a5ff; --docs-surface: #312d4b; --docs-tint: #39304f; --docs-line: rgba(231,227,252,.16); }
  h1,h2,h3 { color: var(--docs-heading); font-weight: 600; line-height: 1.35; text-align: left; }
  h1 { font-size: clamp(1.85rem,3vw,2.6rem); letter-spacing: -.03em; margin-bottom: 18px; }
  h2 { font-size: 1.35rem; margin-bottom: 14px; scroll-margin-top: 90px; }
  h3 { font-size: 1.1rem; }
  p { margin-bottom: 16px; }
  a { color: var(--docs-accent); text-decoration: none; border-radius: 0; }
  a:hover { text-decoration: underline; text-underline-offset: 4px; }
  a:focus-visible, button:focus-visible, summary:focus-visible, input:focus-visible { outline: 2px solid var(--docs-accent); outline-offset: 4px; }
  button { color: inherit; }
}
.docs-skip { position: absolute; left: -10000px; &:focus { left: 24px; top: 72px; z-index: 10; background: var(--docs-surface); padding: 12px; } }
.docs-header { padding: 26px 32px; background: var(--docs-surface); border: 1px solid var(--docs-line); border-radius: 8px; margin-bottom: 28px; }
.docs-brand { display: inline-block; font-size: .8rem; font-weight: 600; letter-spacing: .05em; margin-bottom: 20px; }
.docs-search-label { display: block; color: var(--docs-heading); font-weight: 600; margin-bottom: 8px; }
.docs-search { display: flex; gap: 12px; align-items: center; border: 1px solid var(--docs-line); border-radius: 6px; padding: 0 14px; min-height: 52px; background: var(--docs-surface); }
.docs-search input { min-width: 0; width: 100%; height: 50px; color: var(--docs-heading); outline-offset: 0; }
.docs-search input::placeholder { color: var(--docs-text); opacity: .8; }
.docs-search button { font-size: .85rem; color: var(--docs-accent); min-height: 44px; }
.docs-devices { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 16px; align-items: center; font-size: .8rem; }
.docs-devices span { margin-right: 4px; }
.docs-devices button { padding: 6px 12px; min-height: 36px; border: 1px solid var(--docs-line); border-radius: 5px; }
.docs-devices button[aria-pressed="true"] { color: var(--docs-accent); border-color: var(--docs-accent); background: var(--docs-tint); }
.docs-layout { display: grid; grid-template-columns: 240px minmax(0,1fr); gap: 28px; align-items: start; }
.docs-sidebar { position: sticky; top: 82px; max-height: calc(100vh - 110px); overflow-y: auto; font-size: .875rem; padding-right: 12px; }
.docs-sidebar summary { cursor: pointer; padding: 10px 0; color: var(--docs-heading); font-weight: 600; }
.docs-sidebar nav { margin-top: 8px; }
.docs-sidebar nav > details { border-bottom: 1px solid var(--docs-line); padding: 4px 0; }
.docs-sidebar nav a { display: block; padding: 8px 10px; line-height: 1.55; margin: 3px 0; border: 0; }
.docs-sidebar a[aria-current="page"] { background: var(--docs-tint); font-weight: 600; border-left: 2px solid var(--docs-accent); }
.docs-sidebar-help { display: block; margin: 20px 0; }
.docs-main { min-width: 0; padding: 32px 38px; background: var(--docs-surface); border: 1px solid var(--docs-line); border-radius: 8px; scroll-margin-top: 82px; }
.docs-main:focus { outline: none; }
.docs-eyebrow { display: block; color: var(--docs-accent); font-size: .72rem; font-weight: 600; letter-spacing: .08em; text-transform: uppercase; margin-bottom: 10px; }
.docs-lead { font-size: 1.12rem; line-height: 1.65; }
.docs-start,.docs-note,.docs-device-note { border-left: 3px solid var(--docs-accent); padding: 18px 22px; background: var(--docs-tint); margin: 26px 0; }
.docs-start p,.docs-device-note p { margin: 6px 0 10px; }
.docs-browse-title { margin-top: 34px; }
.docs-categories { display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: 0 28px; }
.docs-category { border-top: 1px solid var(--docs-line); padding: 22px 0; }
.docs-category-title { display: flex; align-items: center; min-height: 44px; margin-bottom: 10px; }
.docs-category-title h3 { color: var(--docs-heading); }
.docs-category-title span { color: var(--docs-accent); font-weight: 400; }
.docs-category p { font-size: .9rem; line-height: 1.65; }
.docs-category ul { list-style: none; padding: 0; }
.docs-category li { margin: 0; }
.docs-category li a { display: block; padding: 6px 0; font-size: .875rem; line-height: 1.55; }
.docs-breadcrumbs { display: flex; flex-wrap: wrap; gap: 8px; font-size: .8rem; margin-bottom: 22px; }
.docs-toc { display: flex; flex-direction: column; border-top: 1px solid var(--docs-line); border-bottom: 1px solid var(--docs-line); padding: 16px 0; gap: 6px; font-size: .875rem; margin: 28px 0; }
.docs-toc a { padding: 3px 0; }
.docs-section { margin: 30px 0; }
.docs-section ol { padding-left: 24px; }
.docs-section li { padding-left: 6px; margin-bottom: 12px; }
.docs-related { border-top: 1px solid var(--docs-line); margin-top: 32px; padding-top: 24px; }
.docs-related a { display: block; padding: 6px 0; }
.docs-actions { display: flex; flex-wrap: wrap; gap: 12px 20px; margin-top: 24px; font-weight: 600; }
.docs-feedback { display: flex; flex-wrap: wrap; gap: 4px 20px; border-top: 1px solid var(--docs-line); margin-top: 32px; padding-top: 22px; font-size: .875rem; }
.docs-feedback p { width: 100%; margin-bottom: 4px; }
.docs-results > a { display: block; padding: 22px 0; border-bottom: 1px solid var(--docs-line); }
.docs-results p { color: var(--docs-text); margin: 0; }
.docs-results h2 { margin-bottom: 8px; }
@media(max-width:959px) {
  .docs-layout { grid-template-columns: minmax(0,1fr); gap: 18px; }
  .docs-sidebar { position: static; max-height: none; padding: 14px 20px; border: 1px solid var(--docs-line); border-radius: 6px; background: var(--docs-surface); }
  .docs-sidebar-help { margin: 8px 0; }
  .docs-main { padding: 28px; }
  .docs-devices button { min-height: 44px; }
}
@media(max-width:599px) {
  .docs-page { margin: 16px auto 32px; font-size: 15px; }
  .docs-header { padding: 20px 16px; margin-bottom: 16px; }
  .docs-search { gap: 8px; padding: 0 10px; }
  .docs-search input { font-size: 16px; }
  .docs-devices span { flex-basis: 100%; }
  .docs-main { padding: 24px 18px; }
  .docs-categories { grid-template-columns: minmax(0,1fr); }
  .docs-start,.docs-note,.docs-device-note { padding: 16px; }
  .docs-sidebar a,.docs-toc a,.docs-related a,.docs-category li a { min-height: 44px; }
}
</style>
